import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

interface CompanySkeletonProps {
  variant?:
    | 'overview'
    | 'financials'
    | 'dividends'
    | 'ratios'
    | 'segments'
    | 'historical-earnings'
    | 'news'
    | 'earnings-call';
}

export function CompanySkeleton({
  variant = 'overview',
}: CompanySkeletonProps) {
  return (
    <div className="space-y-6">
      {/* Page Header Skeleton */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <Skeleton className="h-9 w-[280px]" /> {/* Page title */}
        <Skeleton className="h-10 w-[300px]" /> {/* Search bar */}
      </div>

      {/* Tab Navigation Skeleton */}
      <div className="border-b">
        <div className="flex items-center gap-2 mb-4">
          <Skeleton className="h-8 w-[120px]" /> {/* Active tab */}
          <Skeleton className="h-8 w-[100px]" /> {/* Additional tab */}
          <Skeleton className="h-8 w-[24px]" /> {/* Add button */}
        </div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-6 w-[80px]" /> {/* Tab nav item */}
            <Skeleton className="h-6 w-[100px]" /> {/* Tab nav item */}
            <Skeleton className="h-6 w-[90px]" /> {/* Tab nav item */}
          </div>
          <Skeleton className="h-8 w-[80px]" /> {/* Notes button */}
        </div>
      </div>

      {/* Company Header Skeleton */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Skeleton className="size-12 rounded-lg" /> {/* Company logo */}
              <div>
                <Skeleton className="h-7 w-[200px] mb-2" /> {/* Company name */}
                <Skeleton className="h-4 w-[150px]" /> {/* Company ticker */}
              </div>
            </div>
            <div className="text-right">
              <Skeleton className="h-8 w-[120px] mb-2" /> {/* Stock price */}
              <Skeleton className="h-4 w-[100px]" /> {/* Price change */}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i}>
                <Skeleton className="h-4 w-[80px] mb-1" /> {/* Metric label */}
                <Skeleton className="h-6 w-[100px]" /> {/* Metric value */}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Content Area Skeleton - Varies by page */}
      <ContentSkeleton variant={variant} />
    </div>
  );
}

function ContentSkeleton({
  variant,
}: {
  variant: CompanySkeletonProps['variant'];
}) {
  switch (variant) {
    case 'overview':
      return <OverviewContentSkeleton />;
    case 'financials':
      return <FinancialsContentSkeleton />;
    case 'dividends':
      return <DividendsContentSkeleton />;
    case 'ratios':
      return <RatiosContentSkeleton />;
    case 'segments':
      return <SegmentsContentSkeleton />;
    case 'historical-earnings':
      return <HistoricalEarningsContentSkeleton />;
    case 'news':
      return <NewsContentSkeleton />;
    case 'earnings-call':
      return <EarningsCallContentSkeleton />;
    default:
      return <OverviewContentSkeleton />;
  }
}

function OverviewContentSkeleton() {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Key Metrics Card */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[120px]" />
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex justify-between">
              <Skeleton className="h-4 w-[100px]" />
              <Skeleton className="h-4 w-[80px]" />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Company Info Card */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[140px]" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <div className="pt-2">
            <Skeleton className="h-4 w-[80px] mb-2" />
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-[60px]" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FinancialsContentSkeleton() {
  return (
    <div className="space-y-6">
      {/* Financial Statement Tabs */}
      <div className="flex gap-2 border-b">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-[120px]" />
        ))}
      </div>

      {/* Chart Area */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[200px]" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

function DividendsContentSkeleton() {
  return (
    <div className="space-y-6">
      {/* Charts Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-[150px]" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-[300px] w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-[120px]" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-[300px] w-full" />
          </CardContent>
        </Card>
      </div>

      {/* Timeline Table */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[180px]" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[200px] w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

function RatiosContentSkeleton() {
  return (
    <div className="space-y-6">
      {/* Ratio Categories */}
      <div className="flex gap-2 border-b">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-[100px]" />
        ))}
      </div>

      {/* Ratios Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 9 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <Skeleton className="h-4 w-[120px] mb-2" />
              <Skeleton className="h-6 w-[80px]" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function SegmentsContentSkeleton() {
  return (
    <div className="space-y-6">
      {/* Segment Chart */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[180px]" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>

      {/* Segment Details */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-[140px]" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-[250px] w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-[160px]" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-[250px] w-full" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function HistoricalEarningsContentSkeleton() {
  return (
    <div className="space-y-6">
      {/* Earnings Chart */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[200px]" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[350px] w-full" />
        </CardContent>
      </Card>

      {/* Earnings Table */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[180px]" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex justify-between">
                <Skeleton className="h-4 w-[100px]" />
                <Skeleton className="h-4 w-[80px]" />
                <Skeleton className="h-4 w-[80px]" />
                <Skeleton className="h-4 w-[60px]" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EarningsCallContentSkeleton() {
  return (
    <div className="space-y-6">
      {/* Transcript Selection */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[200px]" />
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border rounded-lg p-4">
                <Skeleton className="h-4 w-[80px] mb-2" />
                <Skeleton className="h-5 w-[120px] mb-1" />
                <Skeleton className="h-3 w-[100px]" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Transcript Content */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[180px]" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i}>
                <Skeleton className="h-4 w-[120px] mb-2" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function NewsContentSkeleton() {
  return (
    <div className="space-y-6">
      {/* News Header */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-[150px]" />
        </CardHeader>
      </Card>

      {/* News Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 9 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              {/* News Image */}
              <Skeleton className="h-[180px] w-full rounded-lg mb-4" />

              {/* News Title */}
              <Skeleton className="h-5 w-full mb-2" />
              <Skeleton className="h-5 w-3/4 mb-3" />

              {/* News Meta */}
              <div className="flex justify-between items-center mb-3">
                <Skeleton className="h-4 w-[80px]" /> {/* Publisher */}
                <Skeleton className="h-4 w-[60px]" /> {/* Date */}
              </div>

              {/* News Preview */}
              <div className="space-y-2">
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3" />
              </div>

              {/* Read More Button */}
              <div className="mt-3 flex justify-end">
                <Skeleton className="h-4 w-[60px]" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex justify-center gap-2">
        <Skeleton className="h-9 w-[100px]" />
        <Skeleton className="h-9 w-[80px]" />
      </div>
    </div>
  );
}
